import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Download, Eye, ThumbsUp, ThumbsDown } from 'lucide-react';
import chattyLogo from '../assets/chatty-logo.png';
import config from '../config';

// FAQ sources (JSON) link to an API path that renders them as PDF.
// It needs the auth token, so it has to be fetched instead of opened directly.
export function isApiPdfUrl(url) {
  return url.startsWith('/api/v1/sources/pdf');
}

function getCompanySourceType(sourceName) {
  const normalizedName = sourceName.toLowerCase().replace(/\.[^.]+$/, '').replace(/[\s-]+/g, '_');
  if (normalizedName === 'company_profile') return 'profile';
  if (normalizedName === 'company_homepage') return 'homepage';
  return null;
}

function getCompanySourceUrl(sourceType, language) {
  if (sourceType === 'profile') {
    return language === 'EN' ? config.companyProfileEnglishUrl : config.companyProfileUrl;
  }
  if (sourceType === 'homepage') {
    return language === 'EN' ? config.companyHomepageEnglishUrl : config.companyHomepageUrl;
  }
  return '';
}

function SourceLink({ source, language, promptLanguage, onClick, onDownload }) {
  const sourceName = typeof source === 'string' ? source : source.name || 'Unknown Source';
  const companySourceType = getCompanySourceType(sourceName);
  const sourceUrl = typeof source === 'object' ? source.url || '' : '';
  const linkUrl = companySourceType ? getCompanySourceUrl(companySourceType, promptLanguage || language) : '';
  const isPdf = sourceName.toLowerCase().endsWith('.pdf') || sourceUrl.toLowerCase().includes('.pdf') || isApiPdfUrl(sourceUrl);
  const shouldLinkSourceName = companySourceType && linkUrl;
  const shouldShowFileActions = !companySourceType && sourceUrl;
  const sourceLabelClassName = 'inline-block text-sm font-medium truncate max-w-[200px] md:max-w-[300px] text-gray-700 dark:text-gray-200';

  return (
    <div className="inline-flex items-center gap-3 group py-1 flex-wrap">
      {shouldLinkSourceName ? (
        <a
          href={linkUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`${sourceLabelClassName} underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-thinking-primary`}
          title={sourceName}
        >
          {sourceName}
        </a>
      ) : (
        <span
          className={sourceLabelClassName}
          title={sourceName}
        >
          {sourceName}
        </span>
      )}

      <div className="flex items-center gap-0.5 opacity-80 group-hover:opacity-100 transition-opacity">
        {shouldShowFileActions && isPdf && (
          <button
            type="button"
            onClick={(event) => onClick?.(source, event.currentTarget)}
            className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium transition-all duration-200 text-blue-500 hover:text-blue-600 hover:bg-black/5 dark:text-blue-400 dark:hover:text-blue-300 dark:hover:bg-white/10"
            title="View PDF"
            aria-label={`View ${sourceName}`}
          >
            <Eye size={14} />
          </button>
        )}

        {shouldShowFileActions && (
          <button
            type="button"
            onClick={() => onDownload?.(source)}
            className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium transition-all duration-200 text-blue-500 hover:text-blue-600 hover:bg-black/5 dark:text-blue-400 dark:hover:text-blue-300 dark:hover:bg-white/10"
            title="Download Source File"
            aria-label={`Download ${sourceName}`}
          >
            <Download size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

const MessageItem = React.memo(function MessageItem({
  message,
  messageIndex,
  onFeedback,
  username,
  language,
  onSourceClick,
  onDocumentDownload,
}) {
  return (
    <div className={`flex ${message.type === 'user' ? 'justify-end gap-2' : 'justify-start gap-2'}`}>
      {message.type === 'bot' && (
        <img
          src={chattyLogo}
          alt="Chatty"
          className="w-7 h-7 rounded-full object-cover flex-shrink-0 mt-2"
        />
      )}
      <div className="max-w-[85%] md:max-w-2xl px-1 py-1 rounded-lg break-words overflow-hidden bg-light-msg text-light-text dark:bg-sidebar dark:text-primary">
        <div className="px-3 py-3 rounded-lg">
          {message.isThinking ? (
            <div className="flex items-center gap-3 animate-pulse">
              <span className="flex items-center font-medium">
                {message.statusLabel || 'Thinking'}
                <span className="ml-1 animate-bounce">.</span>
                <span className="animate-bounce delay-150">.</span>
                <span className="animate-bounce delay-300">.</span>
              </span>
            </div>
          ) : (
            <ReactMarkdown>{message.text.replace(/\n/g, '  \n')}</ReactMarkdown>
          )}
        </div>

        {/* No sources (greetings, not-found answers): hide the badge too, a "0%" next to a redirect reads as an error */}
        {message.type === 'bot' && !message.isThinking && message.sources && message.sources.length > 0 && (
          <div className="px-3 pb-2 pt-1 space-y-2">
            {message.confidence != null && (
              <div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  message.confidence >= 0.8 ? 'bg-green-500/20 text-green-400' :
                  message.confidence >= 0.5 ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-red-500/20 text-red-400'
                }`}>
                  confidence: {Math.round(message.confidence * 100)}%
                </span>
              </div>
            )}

            <div className="text-xs">
              <span className="font-semibold text-blue-600 dark:text-blue-400">
                📄 Sources:
              </span>
              <ul className="mt-1 ml-4 space-y-0.5">
                {message.sources.map((source, index) => (
                  <li key={index} className="text-gray-700 dark:text-gray-300">
                    • <SourceLink source={source} language={language} promptLanguage={message.promptLanguage} onClick={onSourceClick} onDownload={onDocumentDownload} />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {message.type === 'bot' && !message.isThinking && !message.isError && message.text && (
          <div className="px-3 pb-2 flex justify-end items-center gap-1">
            <button
              onClick={() => onFeedback?.(messageIndex, 'thumbs_up')}
              disabled={!message.messageId || !!message.feedback}
              className={`p-1 rounded transition ${
                message.feedback === 'thumbs_up'
                  ? 'text-green-400'
                  : message.feedback
                    ? 'text-gray-500 cursor-default'
                    : 'text-gray-400 hover:text-green-500 dark:text-secondary dark:hover:text-green-400'
              }`}
              title="Helpful"
            >
              <ThumbsUp size={14} />
            </button>
            <button
              onClick={() => onFeedback?.(messageIndex, 'thumbs_down')}
              disabled={!message.messageId || !!message.feedback}
              className={`p-1 rounded transition ${
                message.feedback === 'thumbs_down'
                  ? 'text-red-400'
                  : message.feedback
                    ? 'text-gray-500 cursor-default'
                    : 'text-gray-400 hover:text-red-500 dark:text-secondary dark:hover:text-red-400'
              }`}
              title="Not helpful"
            >
              <ThumbsDown size={14} />
            </button>
          </div>
        )}
      </div>
      {message.type === 'user' && username && (
        <div className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-2 bg-thinking-primary text-accent-text">
          {username.charAt(0).toUpperCase()}
        </div>
      )}
    </div>
  );
});

export default MessageItem;
